// Composition root — the single place where adapters meet domain ports.
// Mirror of `theplaidoyer-frontend/src/container.ts` discipline. The UI
// layer reads ports from here (never imports `adapters/*` directly).

import { createAuthHttp } from '@/adapters/http/auth-http'
import { createAuthStorage } from '@/adapters/storage/auth-storage'
import {
  createContentControlAdapter,
  type ContentControlAdapter,
} from '@/adapters/office/content-control'
import {
  createCustomXmlPartAdapter,
  type CustomXmlPartAdapter,
} from '@/adapters/office/custom-xml-part'
import { createCitationResolverHttp } from '@/adapters/http/citation-resolver-http'
import { createPreflightHttp } from '@/adapters/http/preflight-http'
import { createSearchHttp } from '@/adapters/http/search-http'
import { createCaseHttp } from '@/adapters/http/case-http'
import { createCaseRefsHttp } from '@/adapters/http/case-refs-http'
import { createCommentsHttp } from '@/adapters/http/comments-http'
import { createAgentStreamHttp } from '@/adapters/sse/agent-stream'
import type { AuthPort, AuthStoragePort } from '@/domain/auth/ports'
import type { ChipResolverPort } from '@/domain/citation-chip/ports'
import type { PreflightRepository } from '@/domain/preflight'
import type { SearchPort } from '@/domain/sources/ports'
import type { CaseListPort } from '@/domain/case/ports'
import type { CaseRefsPort } from '@/domain/case-refs/ports'
import type { CommentsPort } from '@/domain/comments/ports'
import type { AgentStreamPort } from '@/domain/agent/ports'

const API_URL =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ||
  'https://api.theplaidoyer.com'

interface Container {
  authHttp: AuthPort
  authStorage: AuthStoragePort
  // PR-3 binding model — citation chips + doc-level XmlPart + resolver.
  // PR-3a shipped the Office.js binding + a fixture resolver; PR-3b
  // (this commit, THE-451) swaps the resolver to the live HTTP adapter
  // wrapping ``POST /api/v1/citation-registry/resolve`` (api#580). The
  // fixture stays in ``src/adapters/office/citation-resolver-fixture.ts``
  // for unit tests + ``setContainer`` overrides.
  citationStore: ContentControlAdapter
  documentMetadata: CustomXmlPartAdapter
  citationResolver: ChipResolverPort
  // PR-3c (THE-451) — ``Vérifier tout`` flow consumer of
  // ``POST /api/v1/citation-registry/preflight`` (api#580). Shares the
  // ``authStorageSingleton`` bearer-token lifecycle with ``citationResolver``.
  preflightRepository: PreflightRepository
  // PR-4a (M2) — live Sources tab search. Fans out to
  // ``POST /api/v1/search/fragments`` (articles) + ``/jurisprudence/search``
  // (case law). Shares the ``authStorageSingleton`` bearer lifecycle.
  search: SearchPort
  // case-context — lists the lawyer's cases + a case's briefs (écritures)
  // for the picker. Supplies the XmlPart anchor (caseId/briefId/version/lang).
  caseList: CaseListPort
  // F1 PR-4 — case reference directory (BE-numbered pièces/faits/moyens).
  // Source of the citable Pièces in the Sources tab + the « Vérifier tout »
  // pièce validate path (PR-5). Wraps GET /api/v1/cases/{id}/refs.
  caseRefs: CaseRefsPort
  // F-COMMENTS (slice A) — review-comment threads on the active brief.
  // Wraps GET/POST/PATCH /api/v1/case-ecritures/{ecriture_id}/threads...
  // (the brief IS a case-écriture). List + reply + resolve/reopen; the
  // anchored-create piece is deferred (F-COMMENTS-ANCHOR). Shares the
  // ``authStorageSingleton`` bearer lifecycle.
  comments: CommentsPort
  // F-AGENT PR-A — agent chat SSE stream. Wraps POST /api/v1/agent/stream
  // (the same agent the web SaaS consumes — data-consumption, not a new
  // LLM touchpoint). Shares the ``authStorageSingleton`` bearer lifecycle.
  agentStream: AgentStreamPort
}

// Bootstrap the AuthStoragePort once so the live resolver can read the
// current bearer token lazily (at every request, not at construction —
// the lawyer can log in / out mid-session and the resolver must follow).
const authStorageSingleton = createAuthStorage()

let container: Container = {
  authHttp: createAuthHttp({ baseUrl: API_URL }),
  authStorage: authStorageSingleton,
  citationStore: createContentControlAdapter(),
  documentMetadata: createCustomXmlPartAdapter(),
  citationResolver: createCitationResolverHttp({
    baseUrl: API_URL,
    getAccessToken: () => authStorageSingleton.read()?.accessToken ?? null,
  }),
  preflightRepository: createPreflightHttp({
    baseUrl: API_URL,
    getAccessToken: () => authStorageSingleton.read()?.accessToken ?? null,
  }),
  search: createSearchHttp({
    baseUrl: API_URL,
    getAccessToken: () => authStorageSingleton.read()?.accessToken ?? null,
  }),
  caseList: createCaseHttp({
    baseUrl: API_URL,
    getAccessToken: () => authStorageSingleton.read()?.accessToken ?? null,
  }),
  caseRefs: createCaseRefsHttp({
    baseUrl: API_URL,
    getAccessToken: () => authStorageSingleton.read()?.accessToken ?? null,
  }),
  comments: createCommentsHttp({
    baseUrl: API_URL,
    getAccessToken: () => authStorageSingleton.read()?.accessToken ?? null,
  }),
  agentStream: createAgentStreamHttp({
    baseUrl: API_URL,
    getAccessToken: () => authStorageSingleton.read()?.accessToken ?? null,
  }),
}

/** Read-only accessor — components / hooks consume ports via this. */
export function getContainer(): Container {
  return container
}

/** Test seam — override one or more ports from a vitest setup. Test code
 *  imports `setContainer` and replaces specific entries; everything else
 *  stays wired to the real adapters. */
export function setContainer(overrides: Partial<Container>): void {
  container = { ...container, ...overrides }
}
